import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  Button,
  Alert,
  AlertIcon,
  Box,
  Text,
  Spinner,
  useToast
} from '@chakra-ui/react';
import WhatsAppService from '../../../../../../services/whatsapp.service';
import api from '../../../../../../services/api';

/**
 * Modal component for adding a WhatsApp group to a lead via invite link
 * 
 * @param {object} props Component props
 * @param {boolean} props.isOpen Whether the modal is open
 * @param {function} props.onClose Function to close the modal
 * @param {object} props.lead Lead data
 * @param {function} props.onSuccess Callback function when group is successfully added
 */
const WhatsAppGroupInviteModal = ({ isOpen, onClose, lead, onSuccess }) => {
  const [inviteLink, setInviteLink] = useState('');
  const [groupInfo, setGroupInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [step, setStep] = useState('input'); // 'input', 'confirm', 'updating'
  const toast = useToast();

  // Reset state when modal is opened
  React.useEffect(() => {
    if (isOpen) {
      setInviteLink('');
      setGroupInfo(null);
      setError(null);
      setStep('input');
    }
  }, [isOpen]);

  // Handle the invite link input change
  const handleInviteLinkChange = (e) => {
    setInviteLink(e.target.value);
    if (error) setError(null);
  };

  // Validate the entered invite link
  const validateInviteLink = () => {
    if (!inviteLink) {
      setError('Please enter a WhatsApp group invite link');
      return false;
    }

    // Check if it's a valid WhatsApp invite link format
    if (!inviteLink.includes('chat.whatsapp.com/')) {
      setError('Invalid invite link format. Expected format: https://chat.whatsapp.com/INVITECODE');
      return false;
    }

    return true;
  };

  // Get group info from the invite link
  const getGroupInfo = async () => {
    if (!validateInviteLink()) return;

    setLoading(true);
    setError(null);

    try {
      const response = await WhatsAppService.getGroupInviteInfo(inviteLink);
      console.log('[WhatsAppGroupInviteModal] Group info:', response);
      
      // Check if we got valid group info
      if (!response || !response.id) {
        throw new Error('Invalid group information received');
      }
      
      setGroupInfo(response);
      setStep('confirm');
    } catch (err) {
      console.error('[WhatsAppGroupInviteModal] Error fetching group info:', err);
      setError(err.message || 'Failed to get group information. Please check the invite link and try again.');
    } finally {
      setLoading(false);
    }
  };

  // Update the lead with the group information
  const updateLeadWithGroupInfo = async () => {
    if (!groupInfo || !groupInfo.id) {
      setError('No valid group information available');
      return;
    }

    setLoading(true);
    setStep('updating');
    setError(null);
    
    try {
      // Format the group ID as expected by backend (numbers followed by @g.us)
      const groupId = groupInfo.id._serialized || groupInfo.id;
      
      // Group name from information
      const groupName = groupInfo.subject || 'WhatsApp Group';

      // Get the proper lead ID - use the business leadId (which is what the backend expects)
      const leadIdentifier = lead.leadId;
      
      console.log('[WhatsAppGroupInviteModal] Updating lead with ID:', leadIdentifier);

      // Prepare only the fields we want to update
      const updateData = {
        whatsappGroup: groupId,
        whatsappGroupName: groupName
      };

      // Update the lead with group information
      const response = await api.put(`/api/leads/${leadIdentifier}`, updateData);

      console.log('[WhatsAppGroupInviteModal] Lead updated:', response.data);
      
      toast({
        title: 'WhatsApp Group Added',
        description: `Successfully added the group "${groupName}" to the lead.`,
        status: 'success',
        duration: 5000,
        isClosable: true
      });
      
      // Call the success callback with updated lead info
      if (onSuccess) {
        onSuccess({
          ...lead,
          whatsappGroup: groupId,
          whatsappGroupName: groupName
        });
      }
      
      onClose();
    } catch (err) {
      console.error('[WhatsAppGroupInviteModal] Error updating lead:', err);
      setError(err.message || 'Failed to update lead with group information');
      setStep('confirm'); // Go back to confirmation step on error
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {step === 'input' && 'Add WhatsApp Group via Invite'}
          {step === 'confirm' && 'Confirm WhatsApp Group'}
          {step === 'updating' && 'Adding WhatsApp Group...'}
        </ModalHeader>
        <ModalCloseButton />
        
        <ModalBody>
          {error && (
            <Alert status="error" mb={4} borderRadius="md">
              <AlertIcon />
              {error}
            </Alert>
          )}
          
          {/* Input Step */}
          {step === 'input' && (
            <FormControl>
              <FormLabel>WhatsApp Group Invite Link</FormLabel>
              <Input
                placeholder="https://chat.whatsapp.com/INVITECODE"
                value={inviteLink}
                onChange={handleInviteLinkChange}
              />
              <FormHelperText>
                Paste the WhatsApp group invite link to add this group to the lead.
              </FormHelperText>
            </FormControl>
          )}
          
          {/* Confirm Step */}
          {step === 'confirm' && groupInfo && (
            <Box>
              <Text fontWeight="bold" mb={2}>Group Information:</Text>
              <Box p={3} borderWidth="1px" borderRadius="md" mb={3}>
                <Text fontSize="sm"><strong>Name:</strong> {groupInfo.subject || 'Unnamed Group'}</Text>
                <Text fontSize="sm"><strong>Description:</strong> {groupInfo.description || 'No description'}</Text>
                <Text fontSize="sm"><strong>Member Count:</strong> {groupInfo.size || 'Unknown'}</Text>
                <Text fontSize="sm"><strong>Created:</strong> {groupInfo.creation ? new Date(groupInfo.creation * 1000).toLocaleString() : 'Unknown'}</Text>
              </Box>
              
              <Text fontSize="sm" fontStyle="italic">
                Adding this group will update the lead record with this WhatsApp group information.
              </Text>
            </Box>
          )}
          
          {/* Updating Step */}
          {step === 'updating' && (
            <Box display="flex" justifyContent="center" alignItems="center" py={4}>
              <Spinner size="lg" mr={3} />
              <Text>Updating lead with group information...</Text>
            </Box>
          )}
        </ModalBody>
        
        <ModalFooter>
          {step === 'input' && (
            <>
              <Button colorScheme="gray" mr={3} onClick={onClose}>Cancel</Button>
              <Button 
                colorScheme="green" 
                onClick={getGroupInfo} 
                isLoading={loading}
                loadingText="Getting Info"
              >
                Get Group Info
              </Button>
            </>
          )}
          
          {step === 'confirm' && (
            <>
              <Button colorScheme="gray" mr={3} onClick={() => setStep('input')}>
                Back
              </Button>
              <Button 
                colorScheme="green" 
                onClick={updateLeadWithGroupInfo} 
                isLoading={loading}
                loadingText="Updating"
              >
                Add Group to Lead
              </Button>
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default WhatsAppGroupInviteModal;
